import { usePortalLanguage } from "./virtualFarmerI18n"

const id = (value) => Number.isSafeInteger(value) && value > 0
const label = (value) => typeof value === "string" ? value.trim() : ""
const domains = new Set(["aeroponic_stm32", "virtual_farmer_vwc", "research_observation_only"])
const roles = new Set(["primary_system", "environment_station", "substrate_sensor", "plant_sensor", "reference_sensor"])

const roleMatches = (node, role, domain) => {
     if (!roles.has(role) || !domains.has(domain)) return false
     if (node.node_type === "chuck_environment_station") return role === "environment_station"
     if (node.node_type === "aeroponic_controller") return role === "primary_system" && domain === "aeroponic_stm32"
     if (node.node_type === "vwc_controller") return role === "substrate_sensor" || (role === "primary_system" && domain === "virtual_farmer_vwc")
     return ["leaf_sensor", "manual_import"].includes(node.node_type) && role !== "primary_system"
}

// Presentation only. Account identity comes from /auth/me, not the differently
// named legacy portal tenant, a name, an email or a browser viewing preference.
export const deviceRunRows = (research, locations, accountId) => {
     if (!id(accountId) || !Array.isArray(locations)
          || ![research?.nodes, research?.deployments, research?.account_runs].every(Array.isArray)) return null
     if (research.deployments.some((deployment) => !deployment || !Array.isArray(deployment.nodes))) return null
     const allNodes = research.nodes
     return allNodes.filter((node) => node && node.account_id === accountId && id(node.id))
          .filter((node, index, nodes) => nodes.findIndex((other) => other.id === node.id) === index)
          .map((node) => {
               const row = { id: node.id, name: label(node.label), type: node.node_type,
                    status: "unassigned", location: null, run: null, domain: null }
               const status = (value) => ({ ...row, status: value })
               if (allNodes.filter((other) => other?.id === node.id).length !== 1) return status("conflict")
               if (node.active === 0 || node.active === false) return status("inactive")
               if (node.active !== 1 && node.active !== true) return status("incomplete")
               const links = research.deployments.flatMap((deployment) => {
                    if (!deployment || deployment.ended_at != null || !Array.isArray(deployment.nodes)) return []
                    return deployment.nodes.filter((linked) => linked?.id === node.id && linked.unlinked_at == null)
                         .map((linked) => ({ deployment, linked }))
               })
               if (!links.length) return row
               if (links.length !== 1) return status("conflict")
               const { deployment, linked } = links[0]
               if (deployment.account_id !== accountId || linked.account_id !== accountId
                    || linked.node_type !== node.node_type
                    || linked.active !== node.active
                    || research.deployments.filter((item) => item?.id === deployment.id).length !== 1) return status("conflict")
               if (!id(deployment.id) || deployment.ended_at !== null || linked.unlinked_at !== null
                    || !id(deployment.account_run_id) || !roleMatches(node, linked.role, deployment.control_domain)) return status("incomplete")
               const runs = research.account_runs.filter((run) => run?.id === deployment.account_run_id)
               if (!runs.length) return status("incomplete")
               if (runs.length !== 1) return status("conflict")
               const run = runs[0]
               if (run.account_id !== accountId || run.external_location_id !== deployment.external_location_id
                    || run.status !== "active" || run.ended_at !== null) return status("conflict")
               if (research.deployments.filter((item) => item?.account_run_id === run.id
                    && item.control_domain === deployment.control_domain && item.ended_at === null).length !== 1) return status("conflict")
               const matches = locations.filter((location) => typeof location?.id === "string" && location.id === run.external_location_id)
               if (!label(run.external_location_id) || !matches.length) return status("incomplete")
               if (matches.length !== 1) return status("conflict")
               return { ...row, status: "linked", location: label(matches[0].name),
                    run: label(run.name), domain: deployment.control_domain }
          })
}

export default function DeviceRunOverview({ research, locations, accountId, loading = false, error = false }) {
     const { t } = usePortalLanguage()
     const rows = deviceRunRows(research, locations, accountId)
     const type = (value) => {
          const key = `research.nodeType.${value}`
          const translated = t(key)
          return translated === key ? t("mapping.otherNode") : translated
     }
     return <section className="vf-card vf-device-run-overview" data-testid="device-run-overview">
          <div className="vf-card-heading"><h3>{t("mapping.title")}</h3></div>
          <p>{t("mapping.intro")}</p>
          {loading ? <p role="status">{t("research.loading")}</p> : error || !rows ? <p role="status">{t("mapping.unavailable")}</p>
               : !rows.length ? <p>{t("mapping.empty")}</p> : <div className="vf-table-wrap" role="region" aria-label={t("mapping.title")} tabIndex="0">
                    <table><caption className="vf-visually-hidden">{t("mapping.title")}</caption><thead><tr>
                         {["device", "location", "run", "domain", "status"].map((key) => <th key={key} scope="col">{t(`mapping.${key}`)}</th>)}
                    </tr></thead><tbody>{rows.map((row) => <tr key={row.id}>
                         <th scope="row"><strong>{row.name || t("mapping.unnamed")}</strong><small>{type(row.type)}</small></th>
                         <td>{row.status === "linked" ? row.location || t("mapping.unnamed") : "—"}</td><td>{row.status === "linked" ? row.run || t("mapping.unnamed") : "—"}</td><td>{row.domain ? t(`research.controlDomain.${row.domain}`) : "—"}</td>
                         <td><span className={`vf-mapping-state ${row.status}`}>{t(`mapping.state.${row.status}`)}</span></td>
                    </tr>)}</tbody></table>
               </div>}
          <p className="vf-safety-note">{t("mapping.boundary")}</p>
     </section>
}
